import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, FileText, RefreshCw, CalendarX, ShieldCheck } from "lucide-react";
import { Reveal } from "../components/grapho/Shared";

const SECTIONS = [
  {
    id: "what-we-deliver",
    icon: FileText,
    title: "What We Deliver",
    body: "300 DPI transparent PNG files, overnight turnaround, 100% unique designs per client, full commercial use rights transferred on delivery.",
    test: "terms-section-delivery",
  },
  {
    id: "refund-policy",
    icon: RefreshCw,
    title: "Refund Policy",
    body: "Credits are non-refundable once delivered to your account. The $1 Launch Trial exists so you can verify our quality and speed before committing to a monthly plan.",
    test: "terms-section-refund",
  },
  {
    id: "cancellation-policy",
    icon: CalendarX,
    title: "Cancellation Policy",
    body: "Cancel anytime from your dashboard. Your plan remains active until the end of your current billing period. No partial refunds are issued for unused days in the current cycle.",
    test: "terms-section-cancellation",
  },
  {
    id: "ip-protection",
    icon: ShieldCheck,
    title: "IP Protection",
    body: "Every design is cross-checked against US trademarks before delivery to keep your storefront safe.",
    test: "terms-section-ip",
  },
];

export default function TermsPage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hash]);

  return (
    <div data-testid="terms-page" className="min-h-screen px-6 pb-24 pt-28">
      <div className="mx-auto max-w-3xl">
        <Link
          to="/"
          data-testid="terms-back-link"
          className="inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Grapho
        </Link>

        <Reveal>
          <p className="mt-10 font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-400">
            The fine print, minus the fine print
          </p>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Terms of Service
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-400">
            Everything you and Grapho agree to, in plain language. Last updated September 2026.
          </p>
        </Reveal>

        <div className="mt-12 space-y-5">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.08}>
              <section
                id={s.id}
                data-testid={s.test}
                className="rounded-3xl border border-white/10 bg-[#0C1222]/80 p-7 sm:p-8"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-amber-400/30 bg-amber-400/10">
                    <s.icon className="h-5 w-5 text-amber-300" />
                  </span>
                  <h2 className="text-lg font-bold tracking-tight text-white">{s.title}</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-300">{s.body}</p>
              </section>
            </Reveal>
          ))}
        </div>

        <p data-testid="terms-contact-line" className="mt-10 text-center text-xs text-slate-600">
          Questions about these terms? Reach our team any time from your dashboard or the site chat.
        </p>
      </div>
    </div>
  );
}